import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { StyleSheetManager } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { OTHER_SEATING_MAX_LENGTH } from '../semshared/utils/lister';
import { createShadow } from '../helper';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
`;

const Card = styled.div`
  width: min(640px, 100%);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.2);
  padding: 28px 28px 20px;
  box-sizing: border-box;
  font-family: Roboto, sans-serif;
  color: #1f2937;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
`;

const Help = styled.p`
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.45;
  color: #4b5563;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 220px;
  resize: vertical;
  box-sizing: border-box;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 12px;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  line-height: 1.45;
  color: #111827;
`;

const Counter = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 18px;
`;

const ActionButton = styled.button<{ filled?: boolean }>`
  border: 0;
  border-radius: 999px;
  padding: 10px 22px;
  font-size: 15px;
  cursor: pointer;
  background: ${props => (props.filled ? '#AC2A6E' : '#e5e7eb')};
  color: ${props => (props.filled ? '#fff' : '#374151')};
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const OtherSeatingIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
    <rect x="1" y="1" width="6" height="6" rx="1" fill="none" stroke="#082B42" strokeWidth="1.2" />
    <rect x="9" y="1" width="6" height="6" rx="1" fill="none" stroke="#082B42" strokeWidth="1.2" />
    <rect x="1" y="9" width="6" height="6" rx="1" fill="none" stroke="#082B42" strokeWidth="1.2" />
    <rect x="9" y="9" width="6" height="6" rx="1" fill="none" stroke="#082B42" strokeWidth="1.2" />
  </svg>
);

interface OtherSeatingDialogProps {
  open: boolean;
  value: string;
  onCancel: () => void;
  onSave: (value: string) => void;
}

/**
 * Long-text editor for the Other seating option.
 * Rendered in its own shadow root so the widget drawer does not clip it.
 */
const OtherSeatingDialog = ({ open, value, onCancel, onSave }: OtherSeatingDialogProps) => {
  const { t } = useTranslation();
  const [shadow, setShadow] = useState<ShadowRoot | null>(null);
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    const next = createShadow(document.body);
    setShadow(next);
    return () => {
      next.host?.parentNode?.removeChild(next.host);
    };
  }, []);

  useEffect(() => {
    if (open) setDraft(value.slice(0, OTHER_SEATING_MAX_LENGTH));
  }, [open, value]);

  if (!open || !shadow) return null;

  const canSave = draft.trim().length > 0;

  return createPortal(
    <StyleSheetManager target={shadow}>
      <div style={{ all: 'initial' }}>
        <Overlay onMouseDown={onCancel}>
          <Card onMouseDown={event => event.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="other-seating-title">
            <TitleRow>
              <OtherSeatingIcon size={22} />
              <Title id="other-seating-title">{t('widget:otherSeating.title')}</Title>
            </TitleRow>
            <Help>{t('widget:otherSeating.help')}</Help>
            <TextArea
              value={draft}
              maxLength={OTHER_SEATING_MAX_LENGTH}
              aria-label={t('widget:otherSeating.describe')}
              onChange={event => setDraft(event.target.value.slice(0, OTHER_SEATING_MAX_LENGTH))}
            />
            <Counter>
              {draft.length}/{OTHER_SEATING_MAX_LENGTH} {t('widget:otherSeating.characters')}
            </Counter>
            <Actions>
              <ActionButton type="button" onClick={onCancel}>
                {t('widget:otherSeating.cancel')}
              </ActionButton>
              <ActionButton type="button" filled disabled={!canSave} onClick={() => canSave && onSave(draft.trim())}>
                {t('widget:otherSeating.save')}
              </ActionButton>
            </Actions>
          </Card>
        </Overlay>
      </div>
    </StyleSheetManager>,
    shadow,
  );
};

export default OtherSeatingDialog;
